import type {
  InventoryItemRecord,
  SaveStockAdjustmentRequest,
} from '@/renderer/types/inventory';
import { apiError, apiSuccess } from '@/renderer/util/notify';
import {
  Button,
  ConfirmDialog,
  DropdownSelect,
  EmptyState,
} from '@components/common';
import { DatePicker } from '@components/common/DatePicker';
import { FormInput, FormTextarea } from '@components/common/FormField';
import {
  useInventoryItems,
  useInventoryStockLocations,
  useInventorySuppliers,
  usePostStockAdjustment,
  useSaveStockAdjustment,
} from '@hooks/inventory';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectEffectiveOutletId } from '@store/outlets';
import { PackageX, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

interface ReturnLine {
  key: string;
  inventoryItemId: string;
  quantity: string;
  unitCost: string;
  reason: string;
}

interface FormState {
  supplierId: string;
  stockLocationId: string;
  returnDate: string;
  referenceNumber: string;
  notes: string;
  lines: ReturnLine[];
}

const makeKey = () => Math.random().toString(36).slice(2);
const emptyLine = (): ReturnLine => ({
  key: makeKey(),
  inventoryItemId: '',
  quantity: '',
  unitCost: '',
  reason: '',
});

const today = () => new Date().toISOString().split('T')[0];

const initialForm = (): FormState => ({
  supplierId: '',
  stockLocationId: '',
  returnDate: today(),
  referenceNumber: '',
  notes: '',
  lines: [emptyLine()],
});

function ItemRow({
  line,
  items,
  onChange,
  onRemove,
  canRemove,
}: {
  line: ReturnLine;
  items: InventoryItemRecord[];
  onChange: (patch: Partial<ReturnLine>) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  return (
    <div className="grid grid-cols-12 gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
      <div className="col-span-4">
        <DropdownSelect
          label="Item"
          value={line.inventoryItemId}
          searchable
          placeholder="Select item"
          options={items.map((item) => ({
            value: item.id,
            label: item.name + (item.sku ? ` (${item.sku})` : ''),
          }))}
          onChange={(v) => onChange({ inventoryItemId: v })}
        />
      </div>
      <div className="col-span-2">
        <FormInput
          label="Qty returned"
          type="number"
          min="0.0001"
          step="0.0001"
          value={line.quantity}
          onChange={(e) => onChange({ quantity: e.target.value })}
          placeholder="0"
        />
      </div>
      <div className="col-span-2">
        <FormInput
          label="Unit cost"
          type="number"
          min="0"
          step="0.01"
          value={line.unitCost}
          onChange={(e) => onChange({ unitCost: e.target.value })}
          placeholder="Auto"
        />
      </div>
      <div className="col-span-3">
        <FormInput
          label="Reason"
          value={line.reason}
          onChange={(e) => onChange({ reason: e.target.value })}
          placeholder="e.g. damaged, wrong item"
        />
      </div>
      <div className="col-span-1 flex items-end pb-1">
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

export const ReturnToSupplierPage: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = currentUser?.company?.id ?? null;
  const outletId = useAppSelector(selectEffectiveOutletId);

  const [form, setForm] = useState<FormState>(initialForm);
  const [pendingPost, setPendingPost] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const suppliersQuery = useInventorySuppliers({
    companyId: companyId ?? 0,
    status: 'active',
    page: 1,
    pageSize: 200,
  });
  const locationsQuery = useInventoryStockLocations({
    companyId: companyId ?? 0,
    outletId: outletId ?? '',
    page: 1,
    pageSize: 200,
  });
  const itemsQuery = useInventoryItems({
    companyId: companyId ?? 0,
    page: 1,
    pageSize: 200,
    status: 'active',
  });

  const { mutateAsync: saveAdjustment, isPending: isSaving } =
    useSaveStockAdjustment();
  const { mutateAsync: postAdjustment, isPending: isPosting } =
    usePostStockAdjustment();

  const suppliers = suppliersQuery.data?.data ?? [];
  const locations = locationsQuery.data?.data ?? [];
  const items = itemsQuery.data?.data ?? [];

  const setLine = (key: string, patch: Partial<ReturnLine>) => {
    setForm((f) => ({
      ...f,
      lines: f.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)),
    }));
  };

  const addLine = () =>
    setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));

  const removeLine = (key: string) => {
    setForm((f) => ({ ...f, lines: f.lines.filter((l) => l.key !== key) }));
  };

  const isValid =
    !!form.supplierId &&
    !!form.stockLocationId &&
    !!form.returnDate &&
    form.lines.length > 0 &&
    form.lines.every((l) => l.inventoryItemId && Number(l.quantity) > 0);

  const handleSubmit = async () => {
    if (!companyId || !outletId || !isValid) return;
    const supplierName =
      suppliers.find((s) => s.id === form.supplierId)?.name ?? form.supplierId;

    const request: SaveStockAdjustmentRequest = {
      companyId,
      outletId,
      stockLocationId: form.stockLocationId,
      adjustmentType: 'correction',
      adjustmentDate: form.returnDate,
      reference: form.referenceNumber
        ? `RTS-${form.referenceNumber}`
        : `Return to ${supplierName}`,
      notes: form.notes || `Stock returned to supplier: ${supplierName}`,
      lines: form.lines.map((l) => ({
        inventoryItemId: l.inventoryItemId,
        // negative quantity = stock decrease
        quantityAdjusted: -Math.abs(Number(l.quantity)),
        unitCost: l.unitCost ? Number(l.unitCost) : null,
        reason: l.reason || `Return to ${supplierName}`,
      })),
    };

    try {
      const result = await saveAdjustment(request);
      if (!result.success || !result.data?.id) {
        apiError(result.error ?? 'Failed to save return');
        return;
      }
      setPendingPost(result.data.id);
      setShowConfirm(true);
    } catch {
      apiError('Failed to save return to supplier');
    }
  };

  const handlePost = async () => {
    if (!pendingPost) return;
    setShowConfirm(false);
    try {
      await postAdjustment(pendingPost);
      apiSuccess('Return to supplier posted — stock reduced');
      setForm(initialForm());
      setPendingPost(null);
    } catch {
      apiError('Failed to post return');
    }
  };

  if (!companyId || !outletId) {
    return (
      <EmptyState
        title="Context required"
        description="Select an outlet to record a return to supplier."
      />
    );
  }

  const busy = isSaving || isPosting;

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <PackageX size={20} className="text-amber-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Return to supplier
            </h2>
            <p className="text-sm text-gray-500">
              Record stock returned to a supplier. Stock quantities are reduced
              and a correction movement is posted.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 text-sm font-semibold text-gray-700">
            Return details
          </h3>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            <DropdownSelect
              label="Supplier"
              value={form.supplierId}
              searchable
              placeholder="Select supplier"
              options={suppliers.map((s) => ({ value: s.id, label: s.name }))}
              onChange={(v) => setForm((f) => ({ ...f, supplierId: v }))}
            />
            <DropdownSelect
              label="Stock location"
              value={form.stockLocationId}
              placeholder="Select location"
              options={locations.map((l) => ({ value: l.id, label: l.name }))}
              onChange={(v) => setForm((f) => ({ ...f, stockLocationId: v }))}
            />
            <DatePicker
              label="Return date"
              mode="single"
              value={form.returnDate}
              onChange={(v) => setForm((f) => ({ ...f, returnDate: v }))}
            />
            <FormInput
              label="Reference / Delivery note"
              value={form.referenceNumber}
              onChange={(e) =>
                setForm((f) => ({ ...f, referenceNumber: e.target.value }))
              }
              placeholder="e.g. DN-2024-001 (optional)"
            />
            <div className="lg:col-span-2">
              <FormTextarea
                label="Notes"
                value={form.notes}
                onChange={(e) =>
                  setForm((f) => ({ ...f, notes: e.target.value }))
                }
                rows={2}
                placeholder="Optional notes about this return"
              />
            </div>
          </div>
        </div>

        <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">
              Items to return
            </h3>
            <Button variant="outline" size="sm" onClick={addLine}>
              <Plus size={14} />
              Add item
            </Button>
          </div>
          <div className="space-y-2">
            {form.lines.map((line) => (
              <ItemRow
                key={line.key}
                line={line}
                items={items}
                onChange={(patch) => setLine(line.key, patch)}
                onRemove={() => removeLine(line.key)}
                canRemove={form.lines.length > 1}
              />
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => setForm(initialForm())}
            disabled={busy}
          >
            Reset
          </Button>
          <Button onClick={handleSubmit} isLoading={busy} disabled={!isValid}>
            Review & post return
          </Button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showConfirm}
        title="Post return to supplier?"
        message="This will reduce stock quantities for all listed items. This action cannot be undone."
        confirmText="Post return"
        onConfirm={handlePost}
        onClose={() => {
          setShowConfirm(false);
          setPendingPost(null);
        }}
      />
    </>
  );
};
